import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
 Users, Search, Download, Filter, BarChart3, 
 TrendingUp, ChevronRight, Building2, Table2, LayoutGrid, PieChart as PieChartIcon
, ArrowUp, ArrowDown, ArrowUpDown} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
 BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
 PieChart, Pie, Cell, Legend
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Colors for charts
const COLORS = [
 '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
 '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
 '#14b8a6', '#a855f7', '#eab308', '#22c55e', '#0ea5e9'
];

const OwnerStatistics = () => {
 const navigate = useNavigate();
 const { user } = useAuth();
 const { selectedBranch } = useApp();
 
 // Check if user is owner or kepala_cabang for branch filtering
 const isOwnerOrKepala = user?.role === 'owner' || user?.role === 'kepala_cabang';
 
 const [loading, setLoading] = useState(true);
 const [ownerStats, setOwnerStats] = useState([]);
 const [branches, setBranches] = useState([]);
 const [searchTerm, setSearchTerm] = useState('');
 const [branchFilter, setBranchFilter] = useState('all');
 const [viewMode, setViewMode] = useState('table'); // table, cards, chart
 const [sortBy, setSortBy] = useState('customer_count'); // customer_count, name
 const [sortOrder, setSortOrder] = useState('desc');

 useEffect(() => {
 fetchData();
 }, [selectedBranch]);

 const fetchData = async () => {
 try {
 setLoading(true);
 const token = localStorage.getItem('token');
 
 // Build statistics URL with branch filter
 let statsUrl = `${API_URL}/api/v2/owners/statistics`;
 const params = new URLSearchParams();
 
 // Apply branch filter from global context
 if (selectedBranch && selectedBranch !== 'all') {
 params.append('branch_id', selectedBranch);
 }
 
 if (params.toString()) {
 statsUrl += `?${params.toString()}`;
 }

 const [statsRes, branchesRes] = await Promise.all([
 axios.get(statsUrl, {
 headers: { Authorization: `Bearer ${token}` }
 }),
 axios.get(`${API_URL}/api/v2/branches`, {
 headers: { Authorization: `Bearer ${token}` }
 })
 ]);

 if (statsRes.data.success) {
 setOwnerStats(statsRes.data.data);
 }

 if (branchesRes.data.success) {
 setBranches(branchesRes.data.data);
 }
 } catch (error) {
 console.error('Error fetching data:', error);
 toast.error('Gagal memuat data statistik');
 } finally {
 setLoading(false);
 }
 };

 // Filter and sort data
 // Header-click sort overrides legacy dropdown when used
 const headerSort = (key) => {
 if (sortBy === key) {
 setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
 } else {
 setSortBy(key);
 setSortOrder('asc');
 }
 };
 const sortIcon = (key) => {
 if (sortBy !== key) return <ArrowUpDown className="inline w-3 h-3 ml-1 text-slate-400 dark:text-slate-500" />;
 return sortOrder === 'asc'
 ? <ArrowUp className="inline w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />
 : <ArrowDown className="inline w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />;
 };

 const filteredStats = ownerStats
 .filter(owner => {
 const matchesSearch = owner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
 (owner.owner_code || '').toLowerCase().includes(searchTerm.toLowerCase());
 const matchesBranch = branchFilter === 'all' || String(owner.branch_id) === String(branchFilter);
 return matchesSearch && matchesBranch;
 })
 .sort((a, b) => {
 const dir = sortOrder === 'desc' ? -1 : 1;
 let av, bv;
 if (sortBy === 'customer_count') {
 return (a.customer_count - b.customer_count) * dir;
 } else if (sortBy === 'owner_code') {
 av = parseInt(String(a.owner_code || '').replace(/[^0-9]/g, '')) || 0;
 bv = parseInt(String(b.owner_code || '').replace(/[^0-9]/g, '')) || 0;
 return (av - bv) * dir;
 } else if (sortBy === 'branch_name') {
 return (a.branch_name || '').localeCompare(b.branch_name || '') * dir;
 } else if (sortBy === 'name') {
 return a.name.localeCompare(b.name) * dir;
 }
 return 0;
 });

 // Calculate totals
 const totalCustomers = filteredStats.reduce((sum, o) => sum + o.customer_count, 0);
 const totalOwners = filteredStats.length;
 const maxCustomers = Math.max(...filteredStats.map(o => o.customer_count), 1);

 // Navigate to customers page with owner filter
 const handleOwnerClick = (ownerId) => {
 navigate(`/app/customers?owner=${ownerId}`);
 };

 // Export to Excel/CSV
 const handleExport = () => {
 const headers = ['No', 'Kode Owner', 'Nama Owner', 'Cabang', 'Jumlah Pelanggan', 'Persentase'];
 const rows = filteredStats.map((owner, index) => [
 index + 1,
 owner.owner_code || '-',
 owner.name,
 owner.branch_name || '-',
 owner.customer_count,
 `${((owner.customer_count / totalCustomers) * 100).toFixed(1)}%`
 ]);

 const csvContent = [
 headers.join(','),
 ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
 ].join('\n');

 const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
 const link = document.createElement('a');
 link.href = URL.createObjectURL(blob);
 link.download = `statistik_owner_${new Date().toISOString().split('T')[0]}.csv`;
 link.click();
 toast.success('Data berhasil diekspor');
 };

 // Prepare chart data
 const chartData = filteredStats.slice(0, 15).map(owner => ({
 name: owner.name.length > 12 ? owner.name.substring(0, 12) + '...' : owner.name,
 fullName: owner.name,
 value: owner.customer_count
 }));

 const pieData = filteredStats.slice(0, 10).map(owner => ({
 name: owner.name,
 value: owner.customer_count
 }));

 if (loading) {
 return (
 <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center min-h-[400px]">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
 </div>
 );
 }

 return (
 <div className="p-4 md:p-6 space-y-6 ">
 {/* Header */}
 <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
 <div>
 <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Statistik Owner</h1>
 <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
 Jumlah pelanggan berdasarkan owner/pemilik
 </p>
 </div>
 <Button 
 onClick={handleExport} 
 variant="outline"
 className="bg-indigo-600 hover:bg-indigo-700 text-white"
 >
 <Download className="w-4 h-4 mr-2" />
 Export Excel
 </Button>
 </div>

 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/40 dark:to-indigo-800/40 border border-indigo-200 dark:border-indigo-800">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-indigo-600 text-sm font-medium dark:text-indigo-400">Total Pelanggan</p>
 <p className="text-3xl font-bold text-indigo-900 dark:text-indigo-200">{totalCustomers.toLocaleString()}</p>
 </div>
 <div className="w-12 h-12 bg-indigo-200 dark:bg-indigo-800 rounded-full flex items-center justify-center">
 <Users className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
 </div>
 </div>
 </CardContent>
 </Card>

 <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/40 dark:to-emerald-800/40 border border-emerald-200 dark:border-emerald-800">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-emerald-600 text-sm font-medium dark:text-emerald-400">Total Owner</p>
 <p className="text-3xl font-bold text-emerald-900 dark:text-emerald-200">{totalOwners}</p>
 </div>
 <div className="w-12 h-12 bg-emerald-200 dark:bg-emerald-800 rounded-full flex items-center justify-center">
 <Building2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
 </div>
 </div>
 </CardContent>
 </Card>

 <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/40 dark:to-amber-800/40 border border-amber-200 dark:border-amber-800">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-amber-600 text-sm font-medium dark:text-amber-400">Rata-rata / Owner</p>
 <p className="text-3xl font-bold text-amber-900 dark:text-amber-200">
 {totalOwners > 0 ? Math.round(totalCustomers / totalOwners) : 0}
 </p>
 </div>
 <div className="w-12 h-12 bg-amber-200 dark:bg-amber-800 rounded-full flex items-center justify-center">
 <TrendingUp className="w-6 h-6 text-amber-600 dark:text-amber-400" />
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Filters & View Toggle */}
 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="p-4">
 <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
 <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
 {/* Search */}
 <div className="relative">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
 <Input
 placeholder="Cari owner..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10 w-full sm:w-64"
 />
 </div>

 {/* Branch Filter */}
 <select
 value={branchFilter}
 onChange={(e) => setBranchFilter(e.target.value)}
 className="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 dark:text-white"
 >
 <option value="all">Semua Cabang</option>
 {branches.map(branch => (
 <option key={branch.id} value={branch.id}>{branch.name}</option>
 ))}
 </select>

 {/* Sort */}
 <select
 value={`${sortBy}-${sortOrder}`}
 onChange={(e) => {
 const [by, order] = e.target.value.split('-');
 setSortBy(by);
 setSortOrder(order);
 }}
 className="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 dark:text-white"
 >
 <option value="customer_count-desc">Terbanyak</option>
 <option value="customer_count-asc">Tersedikit</option>
 <option value="name-asc">Nama A-Z</option>
 <option value="name-desc">Nama Z-A</option>
 </select>
 </div>

 {/* View Mode Toggle - Modern with Icons */}
 <div className="flex gap-1 bg-slate-100 dark:bg-slate-700 p-1 rounded-xl">
 <button
 onClick={() => setViewMode('table')}
 className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
 viewMode === 'table' 
 ? 'bg-white shadow-md text-indigo-600' 
 : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
 }`}
 title="Tampilan Tabel"
 >
 <Table2 className="w-4 h-4" />
 <span className="hidden sm:inline">Tabel</span>
 </button>
 <button
 onClick={() => setViewMode('cards')}
 className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
 viewMode === 'cards' 
 ? 'bg-white shadow-md text-indigo-600' 
 : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
 }`}
 title="Tampilan Kartu"
 >
 <LayoutGrid className="w-4 h-4" />
 <span className="hidden sm:inline">Kartu</span>
 </button>
 <button
 onClick={() => setViewMode('chart')}
 className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
 viewMode === 'chart' 
 ? 'bg-white shadow-md text-indigo-600' 
 : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
 }`}
 title="Tampilan Grafik"
 >
 <PieChartIcon className="w-4 h-4" />
 <span className="hidden sm:inline">Grafik</span>
 </button>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Content based on view mode */}
 {viewMode === 'table' && (
 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="p-0">
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="bg-slate-50 dark:bg-slate-700 border-b dark:border-slate-600">
 <tr>
 <th className="text-left py-2 px-3 text-[11px] font-semibold text-slate-600 dark:text-slate-300 w-12">No</th>
 <th className="text-left py-2 px-3 text-[11px] font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => headerSort('owner_code')}>Kode{sortIcon('owner_code')}</th>
 <th className="text-left py-2 px-3 text-[11px] font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => headerSort('name')}>Nama Owner{sortIcon('name')}</th>
 <th className="text-left py-2 px-3 text-[11px] font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => headerSort('branch_name')}>Cabang{sortIcon('branch_name')}</th>
 <th className="text-right py-2 px-3 text-[11px] font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-slate-100 select-none dark:hover:bg-slate-700" onClick={() => headerSort('customer_count')}>Jumlah Pelanggan{sortIcon('customer_count')}</th>
 <th className="text-left py-2 px-3 text-[11px] font-semibold text-slate-600 w-44 dark:text-slate-300">Progress</th>
 <th className="text-center py-2 px-3 text-[11px] font-semibold text-slate-600 dark:text-slate-300 w-20">Aksi</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
 {filteredStats.map((owner, index) => (
 <tr key={owner.id} className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
 <td className="py-1.5 px-3 text-[11px] text-slate-600 dark:text-slate-400">{index + 1}</td>
 <td className="py-1.5 px-3">
 <span className="text-[10px] font-mono bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-1.5 py-0.5 rounded">
 {owner.owner_code || '-'}
 </span>
 </td>
 <td className="py-1.5 px-3">
 <span className="text-[11px] font-medium text-slate-800 dark:text-white">{owner.name}</span>
 </td>
 <td className="py-1.5 px-3 text-[11px] text-slate-600 dark:text-slate-400">
 {owner.branch_name || '-'}
 </td>
 <td className="py-1.5 px-3 text-right">
 <span className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400">{owner.customer_count}</span>
 <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-1">
 ({((owner.customer_count / totalCustomers) * 100).toFixed(1)}%)
 </span>
 </td>
 <td className="py-1.5 px-3">
 <div className="w-full bg-indigo-100 dark:bg-indigo-900/50 rounded-full h-1.5">
 <div
 className="bg-indigo-500 h-1.5 rounded-full transition-all"
 style={{ width: `${(owner.customer_count / maxCustomers) * 100}%` }}
 />
 </div>
 </td>
 <td className="py-1.5 px-3 text-center">
 <Button
 size="sm"
 variant="ghost"
 onClick={() => handleOwnerClick(owner.id)}
 className="text-[11px] h-6 px-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
 >
 Lihat <ChevronRight className="w-3 h-3 ml-1" />
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </CardContent>
 </Card>
 )}

 {viewMode === 'cards' && (
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
 {filteredStats.map((owner, index) => (
 <Card 
 key={owner.id} 
 className="hover:shadow-lg transition-shadow cursor-pointer"
 onClick={() => handleOwnerClick(owner.id)}
 >
 <CardContent className="p-4">
 <div className="flex items-start justify-between mb-3">
 <div>
 <p className="text-xs text-slate-400 font-mono dark:text-slate-500">{owner.owner_code || '-'}</p>
 <h3 className="font-semibold text-slate-800 mt-1 dark:text-slate-200">{owner.name}</h3>
 <p className="text-xs text-slate-500 dark:text-slate-400">{owner.branch_name || 'Tanpa Cabang'}</p>
 </div>
 <div 
 className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
 style={{ backgroundColor: COLORS[index % COLORS.length] }}
 >
 {index + 1}
 </div>
 </div>
 
 <div className="mt-4">
 <div className="flex items-end justify-between mb-2">
 <span className="text-3xl font-bold text-slate-800 dark:text-slate-200">{owner.customer_count}</span>
 <span className="text-sm text-slate-500 dark:text-slate-400">pelanggan</span>
 </div>
 <div className="w-full bg-slate-200 rounded-full h-2.5 dark:bg-slate-700">
 <div
 className="h-2.5 rounded-full transition-all"
 style={{ 
 width: `${(owner.customer_count / maxCustomers) * 100}%`,
 backgroundColor: COLORS[index % COLORS.length]
 }}
 />
 </div>
 <p className="text-xs text-slate-400 mt-2 text-right dark:text-slate-500">
 {((owner.customer_count / totalCustomers) * 100).toFixed(1)}% dari total
 </p>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 )}

 {viewMode === 'chart' && (
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 {/* Bar Chart */}
 <Card className="bg-white dark:bg-slate-800">
 <CardHeader>
 <CardTitle className="text-lg flex items-center gap-2">
 <BarChart3 className="w-5 h-5" />
 Top 15 Owner
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="h-[400px]">
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis type="number" />
 <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
 <Tooltip 
 formatter={(value, name, props) => [value, props.payload.fullName]}
 labelFormatter={() => ''}
 />
 <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
 </BarChart>
 </ResponsiveContainer>
 </div>
 </CardContent>
 </Card>

 {/* Pie Chart */}
 <Card className="bg-white dark:bg-slate-800">
 <CardHeader>
 <CardTitle className="text-lg flex items-center gap-2">
 <BarChart3 className="w-5 h-5" />
 Distribusi Top 10 Owner
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="h-[400px]">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie
 data={pieData}
 cx="50%"
 cy="50%"
 innerRadius={60}
 outerRadius={120}
 paddingAngle={2}
 dataKey="value"
 label={({ name, percent }) => `${name.substring(0, 10)}... (${(percent * 100).toFixed(0)}%)`}
 labelLine={false}
 >
 {pieData.map((entry, index) => (
 <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </CardContent>
 </Card>
 </div>
 )}
 </div>
 );
};

export default OwnerStatistics;
